// @/shared/utils/FeatureUtils.ts
import { Quote } from "@/shared/types/Quote.interface";
import { FeatureConfig } from "../types/Features";

export class FeatureUtils {
    /**
     * Generate a consistent query key for feature config caching
     * This method can be extended in the future to include other quote properties
     */
    static generateQueryKey(formId: string, quote: Quote, urlPcode?: string | null): [string, string, { discountCode: string }] {
        const discountCode = FeatureUtils.resolveNormalizedPriorityCode({ quote, urlPcode });

        return ["featureConfig", formId, { discountCode }];
    }

    /**
     * Single source of truth for resolving the "current" priority code.
     * Extracted from formPage.tsx so the page, the SSR features prefetch, the
     * client query key, and the features route can never drift.
     * Precedence is URL-first ("last pcode wins" app convention); `||` so empty
     * strings fall through (matches the features route's historical semantics).
     * Output is ALWAYS lowercase.
     */
    static resolveNormalizedPriorityCode({
        quote,
        urlPcode,
        authenticatedDiscountCode
    }: {
        quote?: Partial<Quote> | null;
        urlPcode?: string | null;
        authenticatedDiscountCode?: string | null;
    }): string {
        const value = authenticatedDiscountCode || urlPcode || quote?.discountCode || quote?.affiliateCode || "spot";
        return value.toLowerCase();
    }

    /**
     * Check if a membership input should be bypassed based on query parameters
     * Checks both current browser params and stored quote params
     */
    static shouldBypassMembershipCheck({
        quote,
        currentParams,
        targetCode,
        bypassParam,
        bypassValue,
        debug
    }: {
        quote: Quote;
        currentParams: Record<string, string>;
        targetCode: string;
        bypassParam: string;
        bypassValue: string;
        debug?: boolean;
    }): boolean {
        if (debug) {
            console.log(`🔍 BYPASS CHECK START`);
            console.log(`📋 QUOTE: `, quote);
            console.log(`🌐 Current Params: `, currentParams);
            console.log(`🎯 Target Code: `, targetCode);
            console.log(`🔑 Bypass Param: `, bypassParam);
            console.log(`✅ Bypass Value: `, bypassValue);
        }

        const authorizationKey = quote?.extra?.authorizationKey;
        const discountCode = (quote.discountCode || quote.affiliateCode || "spot").toLowerCase();
        const storedParams = quote.extra?.queryParams || {};
        const currentParamValue = currentParams[bypassParam];
        const storedParamValue = storedParams[bypassParam];

        if (debug) {
            console.log(`🏷️ Normalized Discount Code: `, discountCode);
            console.log(`💾 Stored Params: `, storedParams);
            console.log(`🌐 Current Param Value for '${bypassParam}': `, currentParamValue);
            console.log(`💾 Stored Param Value for '${bypassParam}': `, storedParamValue);
        }

        // Check if discount code matches
        if (discountCode !== targetCode) {
            if (debug) console.log(`❌ BYPASS DENIED: Discount code '${discountCode}' does not match target '${targetCode}'`);
            return false;
        }

        if (debug) console.log(`✅ Discount code matches target`);

        if (!!authorizationKey) {
            if (debug) console.log(`🔑 Authorization key found, bypassing membership check`);
            return true;
        }

        // Check current browser params (passed from client)
        if (currentParamValue === bypassValue) {
            if (debug) console.log(`✅ BYPASS GRANTED: Current param '${bypassParam}' = '${currentParamValue}' matches bypass value '${bypassValue}'`);
            return true;
        }

        if (debug) console.log(`⚠️ Current param check failed: '${currentParamValue}' !== '${bypassValue}'`);

        // Check stored query params
        if (storedParamValue === bypassValue) {
            if (debug) console.log(`✅ BYPASS GRANTED: Stored param '${bypassParam}' = '${storedParamValue}' matches bypass value '${bypassValue}'`);
            return true;
        }

        if (debug) console.log(`❌ BYPASS DENIED: Stored param '${bypassParam}' = '${storedParamValue}' does not match bypass value '${bypassValue}'`);
        if (debug) console.log(`🔍 BYPASS CHECK END - RESULT: false`);
        return false;
    }

    /**
     * Fetch feature config from the API
     * Centralized method to avoid code duplication
     * Throws errors for caller to handle appropriately
     *
     * @param formId - The form ID
     * @param quote - The quote object
     * @param baseUrl - Optional base URL for server-side calls (defaults to empty for client-side)
     */
    static async fetchFeatureConfig(formId: string, quote: Quote, baseUrl: string = "", searchParams?: Record<string, string>): Promise<FeatureConfig> {
        try {
            const url = `${baseUrl}/api/quote/features/${formId}`;

            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ quote, currentParams: searchParams })
            });

            if (!response.ok) {
                throw new Error(`Feature API responded with ${response.status}`);
            }

            const result = await response.json();
            return { features: result.features || {}, appConfig: result.appConfig };
        } catch (error) {
            throw new Error(`Failed to fetch feature config: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    }
}
